<template>
  <AppShell>
    <section class="view on soa-view-dist" aria-labelledby="dist-title">
      <div class="pagehead">
        <div>
          <h1 id="dist-title">{{ t("dist_h") }}</h1>
          <p class="sub">{{ t("dist_sub") }}</p>
        </div>
        <button v-if="currentBrand && distribution.enabled" type="button" class="btn" @click="openComposer">{{ t("dist_new") }}</button>
      </div>

      <div v-if="quota.length" class="dist-quota" aria-label="This month's drafts">
        <div v-for="row in quota" :key="row.id" class="dist-meter" :class="{ full: row.full }">
          <span class="dist-meter-n">{{ row.used }}<span class="dist-meter-cap">/{{ row.cap }}</span></span>
          <span class="dist-meter-l">{{ row.label }}</span>
          <span class="dist-meter-track"><i :style="{ width: row.pct + '%' }"></i></span>
        </div>
      </div>

      <div v-if="items.length" class="dist-filters" role="group" aria-label="Filter drafts">
        <button type="button" class="atbtn" :class="{ on: !channelFilter }" @click="channelFilter = ''">{{ t("dist_filter_all") }}</button>
        <button v-for="channel in channels" :key="channel.id" type="button" class="atbtn" :class="{ on: channelFilter === channel.id }" @click="channelFilter = channel.id">{{ channel.label }}</button>
      </div>

      <p v-if="loading && !items.length" class="empty" role="status">{{ t("dist_loading") }}</p>
      <p v-else-if="distribution.error" class="empty" role="alert">
        {{ distribution.error }}
        <button type="button" class="atbtn" @click="reload">{{ t("dist_retry") }}</button>
      </p>
      <p v-else-if="!currentBrand" class="empty">{{ t("dist_none_brand") }}</p>
      <p v-else-if="!items.length" class="empty">
        {{ t("dist_empty") }}
        <button type="button" class="atbtn" @click="openComposer">{{ t("dist_empty_cta") }}</button>
      </p>

      <div v-else class="rlog">
        <article
          v-for="item in visible"
          :key="item.item_id"
          class="pl-card dist-row"
          :class="{ isdone: item.status === 'cancelled' }"
          tabindex="0"
          role="button"
          :aria-label="`Open the ${channelLabel(item.channel)} draft: ${draftHeadline(item, 80)}`"
          @click="openDetail(item, $event)"
          @keydown="openDetailByKey(item, $event)"
        >
          <div class="pl-top">
            <span class="pl-chip dist-ch" :class="'dist-ch-' + item.channel">{{ channelLabel(item.channel) }}</span>
            <h2 class="dist-head">{{ draftHeadline(item) }}</h2>
            <span class="pl-chip dist-st" :class="'dist-st-' + statusMeta(item.status).tone">{{ statusMeta(item.status).label }}</span>
            <time class="pl-brand" :datetime="isoStamp(item.created_at)">{{ formatStamp(item.created_at, lang) }}</time>
          </div>
          <p v-if="draftSubline(item)" class="dist-q">{{ t("dist_answers") }} <b>{{ draftSubline(item) }}</b></p>
          <p class="dist-flags">
            <span v-if="item.status === 'cited'" class="pill trk">{{ citedLabel(item, lang) }}</span>
            <span v-if="needsThread(item)" class="pill int">{{ t("dist_thread_missing") }}</span>
            <span v-if="item.due" class="pill int">{{ t("dist_due") }}</span>
            <span v-if="draftAngle(item)" class="pill">{{ draftAngle(item) }}</span>
          </p>
        </article>
      </div>

      <!-- Detail: the draft itself, and the three things a client does with one. -->
      <div v-if="open" class="modal on dist-sheet" @click.self="closeSheet">
        <div ref="sheetEl" class="inner" role="dialog" aria-modal="true" aria-labelledby="dist-sheet-title" @keydown="sheetKeydown">
          <button type="button" class="close" :aria-label="t('dist_close')" @click="closeSheet">×</button>
          <h2 id="dist-sheet-title">{{ draftQuestion(open) || draftHeadline(open, 80) }}</h2>
          <p class="dist-chips">
            <span class="pill src">{{ channelLabel(open.channel) }}</span>
            <span class="pill">{{ statusMeta(open.status).label }}</span>
            <span v-if="draftAngle(open)" class="pill">{{ draftAngle(open) }}</span>
            <span v-if="open.status === 'cited'" class="pill trk">{{ citedLabel(open, lang) }}</span>
            <a v-if="open.target?.source_url" class="pill src dist-srclink" :href="open.target.source_url" target="_blank" rel="noopener">{{ t("dist_source_page") }} ↗</a>
          </p>
          <p class="dist-hint">{{ statusMeta(open.status).hint }}</p>

          <p v-if="open.channel === 'reddit'" class="dist-caveat" role="note">
            <b>{{ t("dist_reddit_rules") }}</b>
            {{ needsThread(open) ? t("dist_thread_caveat") : t("dist_thread_ok") }}
            {{ t("dist_disclosure_note") }}
          </p>
          <p v-if="open.target?.note" class="dist-note">{{ open.target.note }}</p>

          <label class="dist-lbl" for="dist-body">{{ t("dist_body_label") }}</label>
          <textarea id="dist-body" ref="bodyEl" v-model="bodyDraft" class="dist-body" rows="14" spellcheck="true"></textarea>

          <div class="ws-pacts dist-acts">
            <button type="button" class="ws-pact dist-primary" @click="copyBody">{{ copied ? t("dist_copied") : t("dist_copy") }}</button>
            <button type="button" class="ws-pact" :disabled="!dirty || busy === 'save'" @click="saveEdit">{{ busy === "save" ? t("dist_saving") : t("dist_save") }}</button>
            <button type="button" class="ws-pact" :disabled="busy === 'regen'" @click="regenerate">{{ busy === "regen" ? t("dist_regen_busy") : t("dist_regen") }}</button>
          </div>
          <p class="dist-hint">{{ ["sent", "cited"].includes(open.status) ? t("dist_regen_new_row") : t("dist_regen_in_place") }}</p>

          <div class="dist-sent">
            <template v-if="open.sent_url">
              <p class="dist-lbl">{{ t("dist_sent_label_done") }}</p>
              <a class="dist-srclink" :href="open.sent_url" target="_blank" rel="noopener">{{ open.sent_url }}</a>
            </template>
            <template v-else>
              <label class="dist-lbl" for="dist-sent-url">{{ t("dist_sent_label") }}</label>
              <div class="dist-sentrow">
                <input id="dist-sent-url" v-model="sentUrl" class="dist-input" type="url" inputmode="url" :placeholder="t('dist_sent_place')" @keydown.enter.prevent="markSent">
                <button type="button" class="ws-pact" :disabled="busy === 'sent'" @click="markSent">{{ busy === "sent" ? t("dist_sent_busy") : t("dist_sent_btn") }}</button>
              </div>
            </template>
          </div>

          <p v-if="sheetError" class="dist-err" role="alert">{{ sheetError }}</p>
        </div>
      </div>

      <!-- New draft: a channel and a question the client's buyers actually asked. -->
      <div v-if="composing" class="modal on dist-sheet" @click.self="closeComposer">
        <div ref="composerEl" class="inner" role="dialog" aria-modal="true" aria-labelledby="dist-new-title" @keydown="composerKeydown">
          <button type="button" class="close" :aria-label="t('dist_close')" @click="closeComposer">×</button>
          <h2 id="dist-new-title">{{ t("dist_new") }}</h2>
          <p class="dist-hint">{{ t("dist_new_sub") }}</p>

          <p class="dist-lbl" id="dist-ch-label">{{ t("dist_channel") }}</p>
          <div class="dist-chrow" role="radiogroup" aria-labelledby="dist-ch-label">
            <button
              v-for="channel in channels"
              :key="channel.id"
              type="button"
              class="atbtn"
              role="radio"
              :aria-checked="newChannel === channel.id ? 'true' : 'false'"
              :class="{ on: newChannel === channel.id }"
              @click="newChannel = channel.id"
            >{{ channel.label }}</button>
          </div>

          <label class="dist-lbl" for="dist-source">{{ t("dist_source") }}</label>
          <p v-if="sourcesLoading" class="dist-hint">{{ t("dist_loading") }}</p>
          <select v-else id="dist-source" v-model="newSource" class="dist-input">
            <option v-for="option in sources" :key="option.key" :value="option.key">{{ option.label }}</option>
          </select>
          <p v-if="selectedSource?.note" class="dist-hint">{{ selectedSource.note }}</p>
          <p v-if="!sourcesLoading && sources.length <= 1" class="dist-hint">{{ t("dist_sources_none") }}</p>

          <template v-if="newChannel === 'reddit'">
            <label class="dist-lbl" for="dist-target">{{ t("dist_target") }}</label>
            <input id="dist-target" v-model="newTarget" class="dist-input" type="url" inputmode="url" placeholder="https://www.reddit.com/r/…/comments/…">
            <p class="dist-hint">{{ t("dist_target_hint") }}</p>
          </template>

          <div class="ws-pacts dist-acts">
            <button type="button" class="ws-pact dist-primary" :disabled="busy === 'create' || !newSource" @click="generate">{{ busy === "create" ? t("dist_generating") : t("dist_generate") }}</button>
            <button type="button" class="ws-pact" @click="closeComposer">{{ t("dist_cancel") }}</button>
          </div>
          <p v-if="composerError" class="dist-err" role="alert">{{ composerError }}</p>
        </div>
      </div>

      <p class="sr-only" role="status" aria-live="polite">{{ status }}</p>
    </section>
  </AppShell>
</template>

<script setup>
// GEO_EXEC_R14 · Distribution.
//
// The page holds no rules: the labels, the headline of a row, the quota meters and the sentence
// shown for a refusal all come from `api/distribution.js`, where they are unit-tested. What is
// here is the wiring, and two decisions worth naming:
//
// * **Nothing on this page publishes anything.** `Mark as posted` is the client telling us what
//   they did, which is why it takes a link and refuses to believe one it cannot open.
// * **Regenerate says which of two things it will do.** On a draft nobody sent it rewrites the
//   piece in place and costs no slot; on a piece already out in the world it writes a NEW row and
//   takes one, because the old body is what was actually published and a citation gets matched
//   against that string.

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import {
  DISTRIBUTION_CHANNELS,
  channelLabel,
  citedLabel,
  createDraft,
  distributionErrorMessage,
  draftAngle,
  draftHeadline,
  draftQuestion,
  draftSubline,
  formatStamp,
  loadDraftSources,
  markDraftSent,
  needsThread,
  newIdempotencyKey,
  quotaRows,
  regenerateDraft,
  saveDraftEdit,
  statusMeta,
} from "../api/distribution.js";
import AppShell from "../layout/AppShell.vue";
import { currentBrand } from "../stores/brands";
import { distribution, loadDistribution, mergeDistributionItem } from "../stores/distribution.js";
import { useI18n } from "../useI18n";

const { t, lang } = useI18n();
const router = useRouter();

const channels = DISTRIBUTION_CHANNELS;
const channelFilter = ref("");
const open = ref(null);
const composing = ref(false);
const bodyDraft = ref("");
const sentUrl = ref("");
const busy = ref("");
const copied = ref(false);
const status = ref("");
const sheetError = ref("");
const composerError = ref("");
const sources = ref([]);
const sourcesLoading = ref(false);
const newChannel = ref("linkedin");
const newSource = ref("");
const newTarget = ref("");
const sheetEl = ref(null);
const bodyEl = ref(null);
const composerEl = ref(null);
let controller;
let opener = null;
let copiedTimer;

const items = computed(() => distribution.items);
const loading = computed(() => distribution.loading);
const quota = computed(() => quotaRows(distribution.quota));
const visible = computed(() => (channelFilter.value ? items.value.filter((item) => item.channel === channelFilter.value) : items.value));
const dirty = computed(() => open.value && bodyDraft.value.trim() !== String(open.value.current_body || "").trim());
const selectedSource = computed(() => sources.value.find((option) => option.key === newSource.value) || null);

function isoStamp(seconds) {
  return seconds ? new Date(Number(seconds) * 1000).toISOString() : undefined;
}

function reload() {
  controller?.abort();
  controller = new AbortController();
  return loadDistribution(currentBrand.value?.id, { signal: controller.signal });
}

function openDetail(item, event) {
  if (event?.target?.closest("a")) return;
  opener = event?.currentTarget || null;
  open.value = item;
  bodyDraft.value = String(item.current_body || "");
  sentUrl.value = "";
  sheetError.value = "";
  copied.value = false;
  nextTick(() => bodyEl.value?.focus({ preventScroll: true }));
}

function openDetailByKey(item, event) {
  if (event.target !== event.currentTarget || !["Enter", " "].includes(event.key)) return;
  event.preventDefault();
  openDetail(item, event);
}

function closeSheet() {
  open.value = null;
  restoreFocus();
}

function restoreFocus() {
  const target = opener;
  opener = null;
  if (target?.focus) requestAnimationFrame(() => target.focus({ preventScroll: true }));
}

function sheetKeydown(event) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeSheet();
  }
}

function composerKeydown(event) {
  if (event.key === "Escape") {
    event.preventDefault();
    closeComposer();
  }
}

async function copyBody() {
  const text = bodyDraft.value;
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // A browser that refuses the clipboard still has to leave the client a way out: select the
    // text so one keystroke copies it.
    bodyEl.value?.select();
  }
  copied.value = true;
  status.value = "Draft copied.";
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => { copied.value = false; }, 2200);
}

async function saveEdit() {
  if (!open.value || !dirty.value) return;
  busy.value = "save";
  sheetError.value = "";
  try {
    const result = await saveDraftEdit(open.value.item_id, currentBrand.value?.id, bodyDraft.value);
    if (result?.item) {
      mergeDistributionItem(result.item);
      open.value = result.item;
      bodyDraft.value = String(result.item.current_body || "");
    }
    status.value = "Your version saved.";
  } catch (error) {
    sheetError.value = distributionErrorMessage(error);
  } finally {
    busy.value = "";
  }
}

async function markSent() {
  if (!open.value) return;
  busy.value = "sent";
  sheetError.value = "";
  try {
    const result = await markDraftSent(open.value.item_id, currentBrand.value?.id, sentUrl.value.trim());
    if (result?.item) {
      mergeDistributionItem(result.item);
      open.value = result.item;
    }
    status.value = "Marked as posted.";
  } catch (error) {
    // The status does not move on a refused link, and neither does the row: the client's typed
    // URL stays in the box so they can fix it rather than retype it.
    sheetError.value = distributionErrorMessage(error);
  } finally {
    busy.value = "";
  }
}

async function regenerate() {
  if (!open.value) return;
  busy.value = "regen";
  sheetError.value = "";
  try {
    const result = await regenerateDraft(open.value.item_id, currentBrand.value?.id);
    if (result?.item) {
      mergeDistributionItem(result.item);
      open.value = result.item;
      bodyDraft.value = String(result.item.current_body || "");
    }
    status.value = result?.replaces ? "Rewritten as a new piece." : "Rewritten from another angle.";
    await reload();
  } catch (error) {
    sheetError.value = distributionErrorMessage(error);
  } finally {
    busy.value = "";
  }
}

async function openComposer() {
  composing.value = true;
  composerError.value = "";
  opener = document.activeElement;
  nextTick(() => composerEl.value?.querySelector("button, select, input")?.focus({ preventScroll: true }));
  if (sources.value.length) return;
  sourcesLoading.value = true;
  try {
    sources.value = await loadDraftSources(currentBrand.value?.id);
    newSource.value = sources.value[0]?.key || "";
  } catch (error) {
    composerError.value = distributionErrorMessage(error);
  } finally {
    sourcesLoading.value = false;
  }
}

function closeComposer() {
  composing.value = false;
  restoreFocus();
}

async function generate() {
  const chosen = selectedSource.value;
  if (!chosen) return;
  busy.value = "create";
  composerError.value = "";
  try {
    const result = await createDraft(
      {
        brand_id: currentBrand.value?.id,
        channel: newChannel.value,
        source_type: chosen.source_type,
        source_id: chosen.source_id,
        target_url: newChannel.value === "reddit" ? newTarget.value.trim() : "",
      },
      { idempotencyKey: newIdempotencyKey() },
    );
    composing.value = false;
    newTarget.value = "";
    status.value = "Draft written.";
    await reload();
    if (result?.item) openDetail(result.item, null);
  } catch (error) {
    composerError.value = distributionErrorMessage(error);
  } finally {
    busy.value = "";
  }
}

// The switch is off: there is no sidebar entry either, so a client who typed the URL (or followed
// an old link) goes where the product still works rather than reading an empty page.
const switchedOff = computed(() => distribution.known && !distribution.enabled);

onMounted(() => {
  if (!switchedOff.value) reload();
});
watch(switchedOff, (off) => { if (off) router.replace("/maya"); }, { immediate: true });
watch(() => currentBrand.value?.id, () => { open.value = null; sources.value = []; reload(); });
onBeforeUnmount(() => { controller?.abort(); clearTimeout(copiedTimer); });
</script>

<style scoped>
/* Tokens only — every colour here resolves in both schemes through app.css's :root. */
.soa-view-dist .pagehead h1 {
  margin-bottom: 8px;
}

.dist-quota {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 14px 0 4px;
}

.dist-meter {
  flex: 1 1 200px;
  min-width: 0;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 12px 14px;
}

.dist-meter-n {
  font-size: var(--fs-title2);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--ink);
}

.dist-meter-cap {
  font-size: var(--fs-subhead);
  font-weight: 600;
  color: var(--mute);
}

.dist-meter-l {
  display: block;
  font-size: var(--fs-caption2);
  color: var(--mute);
  margin-top: 1px;
}

.dist-meter-track {
  display: block;
  height: 4px;
  margin-top: 9px;
  border-radius: 100px;
  background: var(--fill-3);
  overflow: hidden;
}

.dist-meter-track i {
  display: block;
  height: 100%;
  border-radius: 100px;
  background: var(--accent);
}

.dist-meter.full .dist-meter-track i {
  background: var(--amber);
}

.dist-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 14px 0 2px;
}

.dist-row {
  cursor: pointer;
  transition: border-color 140ms ease-out, box-shadow 140ms ease-out, transform 90ms ease-out;
}

.dist-row:hover {
  border-color: var(--tint-line);
}

.dist-row:active {
  transform: scale(0.995);
}

.dist-row:focus-visible {
  outline: 3px solid var(--indigo);
  outline-offset: 2px;
}

.dist-head {
  margin: 0;
  font-size: var(--fs-subhead);
  font-weight: 700;
  color: var(--ink);
  flex: 1 1 240px;
  min-width: 0;
}

.dist-ch {
  background: var(--fill);
  color: var(--body);
}

.dist-ch-linkedin {
  background: var(--tint);
  color: var(--indigo);
}

.dist-st-ok {
  background: var(--ok-fill);
  color: var(--jade);
}

.dist-st-cited {
  background: var(--tint-strong);
  color: var(--indigo);
}

.dist-st-draft {
  background: var(--fill);
  color: var(--mute);
}

.dist-st-muted {
  background: none;
  border: 1px dashed var(--line);
  color: var(--mute);
}

.dist-q {
  margin: 7px 0 0;
  font-size: var(--fs-footnote);
  color: var(--mute);
}

.dist-q b {
  color: var(--body);
  font-weight: 600;
}

.dist-flags {
  margin: 7px 0 0;
  min-height: 0;
}

.dist-flags:empty {
  display: none;
}

/* The sheet reuses .modal / .inner from app.css; only the content inside it is ours. */
.dist-sheet .inner {
  max-width: 720px;
}

.dist-chips {
  margin: 6px 0 2px;
}

.dist-hint {
  margin: 6px 0 0;
  font-size: var(--fs-footnote);
  color: var(--mute);
}

.dist-caveat {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--warn-fill);
  color: var(--body);
  font-size: var(--fs-footnote);
}

.dist-caveat b {
  color: var(--ink);
  margin-right: 4px;
}

.dist-note {
  margin: 8px 0 0;
  font-size: var(--fs-footnote);
  color: var(--body);
}

.dist-lbl {
  display: block;
  margin: 16px 0 6px;
  font-size: var(--fs-caption2);
  font-weight: 700;
  letter-spacing: .02em;
  text-transform: uppercase;
  color: var(--mute);
}

.dist-body,
.dist-input {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  font: inherit;
  color: var(--ink);
  background: var(--bg-elevated);
  border: 1px solid var(--line);
  border-radius: 10px;
  padding: 11px 12px;
}

.dist-body {
  font-size: var(--fs-body);
  line-height: 1.55;
  resize: vertical;
  min-height: 180px;
  white-space: pre-wrap;
}

.dist-body:focus-visible,
.dist-input:focus-visible {
  outline: 2px solid var(--indigo);
  outline-offset: 1px;
  border-color: var(--indigo);
}

.dist-acts {
  margin-top: 12px;
  gap: 7px;
}

/* The sheet's controls are the ones a client actually presses, so they get a real target rather
   than the 26px chip the workbench rows use. */
.dist-sheet .ws-pact {
  min-height: 34px;
  padding: 6px 12px;
  font-size: var(--fs-footnote);
}

.dist-primary {
  background: var(--accent-fill);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}

.dist-primary:hover {
  background: var(--accent-press);
  border-color: transparent;
  color: #fff;
}

.dist-primary:active {
  transform: scale(0.985);
}

.dist-sent {
  margin-top: 4px;
}

.dist-sentrow {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.dist-sentrow .dist-input {
  flex: 1 1 260px;
  min-width: 0;
}

.dist-chrow {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dist-srclink {
  color: var(--accent);
  word-break: break-all;
}

.dist-err {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: 10px;
  background: var(--bad-fill);
  color: var(--verm);
  font-size: var(--fs-footnote);
}

@media (max-width: 560px) {
  /* The sheet is the whole screen at this width, so it keeps the page's 16px gutter and no more. */
  .dist-sheet {
    padding: 16px;
  }

  .dist-sheet .inner {
    padding: 20px 18px;
  }

  .dist-sheet .dist-body {
    min-height: 150px;
  }

  .dist-head {
    flex-basis: 100%;
    order: 3;
  }

  .dist-quota {
    gap: 8px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dist-row {
    transition: none;
  }

  .dist-row:active {
    transform: none;
  }
}
</style>
